import { ChevronLeft } from "lucide-react";
import { observer } from "mobx-react";
import { useCallback, useMemo } from "react";
import { useNavigate } from "react-router";
import { BasePage } from "@/components/BasePage";
import LoadFailure from "@/components/common/LoadFailure";
import LoadingSpinner from "@/components/common/LoadingSpinner";
import { useResultsDashboard } from "@/hooks/useResultsDashboard";
import { useRootStore } from "@/state/RootStore";
import type { LoadStatus } from "@/state/loadStatus";
import type { ResultsAttempt } from "@/types/results";
import { PreviousAttemptCard, ResultsEmptyState } from "./ResultsPrimitives";
import { useTranslation } from "react-i18next";

const PastExamsResults = observer(() => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const store = useRootStore();
  const { model } = useResultsDashboard();
  const testsStatus = useMemo<LoadStatus>(() => {
    const tests = store.testStore.testsStatus;
    const retired = store.questionsStore.deletedQuestionsStatus;
    if (tests === "error" || retired === "error") return "error";
    if (tests === "loaded" && retired === "loaded") return "loaded";
    return "loading";
  }, [store.questionsStore.deletedQuestionsStatus, store.testStore.testsStatus]);
  const groupedAttempts = useMemo(() => {
    const groups = new Map<string, ResultsAttempt[]>();
    model?.attempts.forEach((attempt) => {
      const existing = groups.get(attempt.dateLabel);
      if (existing) existing.push(attempt);
      else groups.set(attempt.dateLabel, [attempt]);
    });
    return Array.from(groups.entries());
  }, [model]);

  const retry = useCallback(() => {
    store.testStore.loadTests();
    if (store.questionsStore.deletedQuestionsStatus === "error") {
      store.questionsStore.loadQuestions();
    }
  }, [store]);

  if (testsStatus === "error") {
    return (
      <BasePage activeItem="results" contentClassName="pt-0 md:pt-0">
        <LoadFailure onRetry={retry} />
      </BasePage>
    );
  }

  if (testsStatus !== "loaded") {
    return (
      <BasePage activeItem="results" contentClassName="pt-0 md:pt-0">
        <LoadingSpinner labelKey="results.loading_past_exams_alt" />
      </BasePage>
    );
  }

  if (!model) {
    return (
      <BasePage activeItem="results" contentClassName="pt-0 md:pt-0">
        <div className="flex min-h-full items-center justify-center bg-[#f4f4f6] p-6">
          <ResultsEmptyState
            buttonLabel={t("results.quick_practice_cta")}
            description={t("results.no_results_description")}
            footer={t("results.quick_practice_duration")}
            onAction={() => navigate("/study")}
            title={t("results.no_results_title")}
          />
        </div>
      </BasePage>
    );
  }

  return (
    <BasePage activeItem="results" contentClassName="pt-0 md:pt-0 overflow-hidden">
      <div className="flex h-full min-h-0 flex-col bg-white">
        <header className="shrink-0 border-b border-[#dddfe4]">
          <div className="dp-page-container flex min-h-20 items-center gap-3 px-6 py-4">
            <button
              aria-label={t("results.back_to_results")}
              className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-[#5b616d] outline-none hover:bg-[#f4f4f6] focus-visible:ring-2 focus-visible:ring-[var(--dp-brand-button)]"
              onClick={() => navigate("/results")}
              type="button"
            >
              <ChevronLeft aria-hidden="true" className="h-6 w-6" />
            </button>
            <div>
              <h1 className="text-lg font-semibold leading-[27px] text-[#0a0c11]">{t("results.previous_results")}</h1>
              <p className="text-sm leading-[21px] tracking-[0.07px] text-[#5b616d]">
                {t("results.previous_results_description")}
              </p>
            </div>
          </div>
        </header>
        <div className="dp-page-container flex min-h-0 flex-1 flex-col gap-6 overflow-y-auto p-6">
          {groupedAttempts.map(([dateLabel, attempts]) => (
            <section className="flex flex-col gap-2" key={dateLabel}>
              <h2 className="text-sm font-semibold leading-[21px] tracking-[0.07px] text-[#0a0c11]">{dateLabel}</h2>
              <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
                {attempts.map((attempt) => (
                  <PreviousAttemptCard
                    attempt={attempt}
                    key={attempt.id}
                    onClick={() =>
                      navigate(`/exam-result/${attempt.id}`, {
                        state: { resultOrigin: "past-exams" },
                      })
                    }
                  />
                ))}
              </div>
            </section>
          ))}
        </div>
      </div>
    </BasePage>
  );
});

export default PastExamsResults;
